<template>
  <div class="chart-box insight-card">
    <template v-if="state === 'loading'">
      <div class="insight-skel-header">
        <div class="insight-skel-icon"></div>
        <div class="insight-skel-line w-50"></div>
      </div>
      <div class="insight-skel-line w-90"></div>
      <div class="insight-skel-line w-70"></div>
      <div class="insight-skel-line w-40"></div>
    </template>

    <template v-else-if="state === 'unavailable'">
      <div class="insight-state">
        <span class="material-icons-round insight-state-icon insight-state-icon-muted">info</span>
        <div class="insight-state-title">{{ $t('insightsNotAvailableTitle') }}</div>
        <div class="insight-muted">{{ $t('insightsNotAvailable') }}</div>
      </div>
    </template>

    <template v-else-if="state === 'error'">
      <div class="insight-state">
        <span class="material-icons-round insight-state-icon insight-state-icon-error">error_outline</span>
        <div class="insight-state-title">{{ $t('insightsErrorTitle') }}</div>
        <div class="insight-muted mb-3">{{ $t('insightsError') }}</div>
        <button type="button" class="btn btn-sm btn-outline-primary insight-retry-btn" @click="fetchMetric">
          <span class="material-icons-round">refresh</span>
          {{ $t('insightsRetry') }}
        </button>
      </div>
    </template>

    <template v-else-if="card">
      <div class="insight-header">
        <span class="material-icons-round insight-icon" :class="metricIconClass">{{ metricIcon }}</span>
        <div class="insight-header-text">
          <div class="insight-title">{{ card.title }}</div>
          <div v-if="card.subtitle" class="insight-subtitle">{{ card.subtitle }}</div>
        </div>
        <span v-if="staleTag" class="insight-stale-tag">
          <span class="material-icons-round">schedule</span>{{ staleTag }}
        </span>
      </div>

      <div class="insight-lines">
        <div
          v-for="(line, idx) in parsedLines"
          :key="idx"
          :class="['insight-line', emphasisClass(line.emphasis)]"
        >
          <span class="material-icons-round insight-line-icon">{{ emphasisIcon(line.emphasis) }}</span>
          <div class="insight-line-body">
            <div v-if="line.label" class="insight-line-label">{{ line.label }}</div>
            <div v-if="line.items.length > 1" class="insight-line-items">
              <div v-for="(item, i) in line.items" :key="i" class="insight-line-item">
                <span class="insight-item-name">{{ item.name }}</span>
                <span v-if="item.value" class="insight-item-value">{{ item.value }}</span>
              </div>
            </div>
            <div v-else-if="line.items.length === 1" class="insight-line-single">
              <span class="insight-item-name">{{ line.items[0].name }}</span>
              <span v-if="line.items[0].value" class="insight-item-value">{{ line.items[0].value }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="card.badges && card.badges.length" class="insight-badges">
        <span v-for="(badge, idx) in card.badges" :key="idx" class="insight-badge">{{ badge }}</span>
      </div>

      <div v-if="card.footnote" class="insight-footnote">
        <span class="material-icons-round">auto_awesome</span>{{ card.footnote }}
      </div>
    </template>
  </div>
</template>

<script>
import InsightsService from '@/services/insights.service';

const METRIC_ICONS = {
  winback: 'favorite',
  'quiet-days': 'schedule',
  'menu-engineering': 'restaurant_menu',
  forecast: 'trending_up',
  'outlet-compare': 'storefront',
};

export default {
  name: 'InsightCard',
  props: {
    metric: { type: String, required: true },
    refreshNonce: { type: Number, default: 0 },
  },
  data() {
    return {
      state: 'loading', // loading | ready | error | unavailable
      card: null,
      meta: null,
    };
  },
  computed: {
    metricIcon() {
      return METRIC_ICONS[this.metric] || 'insights';
    },
    metricIconClass() {
      return this.meta && this.meta.entitled === false ? 'insight-icon-locked' : '';
    },
    staleTag() {
      if (!this.meta || !this.meta.stale_s) {
        return '';
      }
      const minutes = Math.round(this.meta.stale_s / 60);
      return this.$t('insightsAsOfMinAgo', { minutes });
    },
    parsedLines() {
      if (!this.card || !this.card.lines) {
        return [];
      }
      return this.card.lines.map((line) => ({ ...line, ...this.splitLine(line.text) }));
    },
  },
  watch: {
    refreshNonce() {
      this.fetchMetric();
    },
  },
  mounted() {
    this.fetchMetric();
  },
  methods: {
    emphasisClass(emphasis) {
      return {
        strong: 'insight-line-strong',
        muted: 'insight-line-muted',
        positive: 'insight-line-positive',
        negative: 'insight-line-negative',
      }[emphasis] || '';
    },
    emphasisIcon(emphasis) {
      return {
        strong: 'star',
        muted: 'remove',
        positive: 'check_circle',
        negative: 'warning',
      }[emphasis] || 'remove';
    },
    // Third-party lines sometimes pack a whole list into one string, e.g.
    // "Top sellers: POS — 61,669 sold, Bibimbap — 36,573 sold" — split it into
    // an optional heading label plus one row per item for readability. Simple
    // lines with nothing to split ("Active (<30 days): 0") render unchanged.
    splitLine(text) {
      const match = /^([^:]{1,40}):\s+(.+)$/s.exec(text);
      const label = match ? match[1] : '';
      const rest = match ? match[2] : text;
      const items = rest.split(/,\s+/).map((s) => s.trim()).filter(Boolean).map((item) => this.splitItem(item));

      const worthSplitting = items.length > 1 || (items.length === 1 && items[0].value);
      if (!worthSplitting) {
        return { label: '', items: [{ name: text, value: '' }] };
      }
      return { label, items };
    },
    // "Name — value" pairs (the API's own convention, e.g. "kayden — 130 days")
    // get the value visually emphasized; plain text falls back to name-only.
    splitItem(item) {
      const idx = item.indexOf(' — ');
      if (idx === -1) {
        return { name: item, value: '' };
      }
      return { name: item.slice(0, idx), value: item.slice(idx + 3) };
    },
    fetchMetric() {
      this.state = 'loading';
      InsightsService.getMetric(this.metric)
        .then((res) => {
          const body = res.data;
          if (!body.result.isSuccess) {
            this.applyErrorState(body.data || {});
            return;
          }
          const envelope = body.data;
          this.meta = envelope.meta || null;
          this.card = envelope.card || null;
          this.state = 'ready';
        })
        .catch((err) => {
          const data = (err.response && err.response.data && err.response.data.data) || {};
          this.applyErrorState(data);
        });
    },
    applyErrorState(data) {
      this.state = data.retryable === false ? 'unavailable' : 'error';
    },
  },
};
</script>

<style scoped>
.chart-box.insight-card {
  background: #ffffff;
  border: 1px solid #f0f1f5;
  border-radius: 16px;
  padding: 20px;
  box-shadow: rgba(20, 40, 100, 0.08) 0px 6px 18px;
  text-align: left;
  height: 100%;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.chart-box.insight-card:hover {
  box-shadow: rgba(20, 40, 100, 0.14) 0px 10px 24px;
  transform: translateY(-2px);
}

.insight-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
}

.insight-icon {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: #eef1fb;
  color: #133067;
  font-size: 22px !important;
}

.insight-icon-locked {
  background: #f3f3f3;
  color: #aaa;
}

.insight-header-text {
  flex: 1 1 auto;
  min-width: 0;
}

.insight-title {
  font-size: 17px;
  color: #133067;
  font-weight: 600;
  line-height: 1.3;
}

.insight-subtitle {
  color: #8a90a2;
  font-size: 12.5px;
  margin-top: 2px;
}

.insight-stale-tag {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: #9aa0b4;
  font-size: 11px;
  white-space: nowrap;
  background: #f5f6fa;
  border-radius: 10px;
  padding: 3px 8px;
}

.insight-stale-tag .material-icons-round {
  font-size: 13px !important;
}

.insight-lines {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
}

.insight-line {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13.5px;
  line-height: 1.5;
  padding: 6px 8px;
  border-radius: 8px;
}

.insight-line-icon {
  font-size: 16px !important;
  margin-top: 1px;
  flex: 0 0 auto;
}

.insight-line-body {
  flex: 1 1 auto;
  min-width: 0;
}

.insight-line-label {
  font-weight: 600;
  margin-bottom: 4px;
}

.insight-line-items {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.insight-line-item,
.insight-line-single {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  font-size: 13px;
}

.insight-line-items .insight-line-item {
  padding: 3px 8px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.55);
}

.insight-line-items .insight-line-item:nth-child(even) {
  background: rgba(255, 255, 255, 0.25);
}

.insight-item-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
  opacity: 0.9;
}

.insight-item-value {
  flex: 0 0 auto;
  font-weight: 700;
  white-space: nowrap;
}

.insight-line-strong {
  font-weight: 600;
  color: #133067;
  background: #eef1fb;
}

.insight-line-strong .insight-line-icon {
  color: #133067;
}

.insight-line-muted {
  color: #8a90a2;
}

.insight-line-muted .insight-line-icon {
  color: #c2c6d4;
}

.insight-line-positive {
  color: #2e9c3a;
  background: #eefbf0;
}

.insight-line-positive .insight-line-icon {
  color: #2e9c3a;
}

.insight-line-negative {
  color: #d3350f;
  background: #fdeeea;
}

.insight-line-negative .insight-line-icon {
  color: #d3350f;
}

.insight-badges {
  margin: 4px 0 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.insight-badge {
  display: inline-block;
  background: #eef1fb;
  color: #133067;
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
}

.insight-footnote {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #b3b7c6;
  font-size: 11px;
  margin-top: 8px;
  padding-top: 10px;
  border-top: 1px dashed #eceef4;
}

.insight-footnote .material-icons-round {
  font-size: 13px !important;
}

.insight-muted {
  color: #8a90a2;
  font-size: 13.5px;
}

.insight-state {
  text-align: center;
  padding: 18px 8px;
}

.insight-state-icon {
  font-size: 34px !important;
  margin-bottom: 8px;
}

.insight-state-icon-muted {
  color: #c2c6d4;
}

.insight-state-icon-error {
  color: #e0793b;
}

.insight-state-title {
  font-size: 15px;
  font-weight: 600;
  color: #133067;
  margin-bottom: 4px;
}

.insight-retry-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 20px;
}

.insight-retry-btn .material-icons-round {
  font-size: 15px !important;
}

.insight-skel-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.insight-skel-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  flex: 0 0 auto;
  background: linear-gradient(90deg, #eee 25%, #f5f5f5 37%, #eee 63%);
  background-size: 400% 100%;
  animation: insight-skeleton-shimmer 1.4s ease infinite;
}

.insight-skel-line {
  height: 13px;
  border-radius: 4px;
  margin-bottom: 10px;
  background: linear-gradient(90deg, #eee 25%, #f5f5f5 37%, #eee 63%);
  background-size: 400% 100%;
  animation: insight-skeleton-shimmer 1.4s ease infinite;
}

.insight-skel-header .insight-skel-line {
  margin-bottom: 0;
  height: 16px;
  flex: 1 1 auto;
}

.insight-skel-line.w-90 {
  width: 90%;
}

.insight-skel-line.w-70 {
  width: 70%;
}

.insight-skel-line.w-50 {
  width: 50%;
}

.insight-skel-line.w-40 {
  width: 40%;
}

@keyframes insight-skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}
</style>
